import { useState, type MouseEvent } from 'react';
import { useMsal } from '@azure/msal-react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LogoutIcon from '@mui/icons-material/Logout';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';

import { loginRequest } from '../auth/msalConfig';
import { useWritePermission } from '../api/permission-queries';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (
    ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() ||
    '?'
  );
}

/**
 * Header user menu: signed-in name + UPN, role, Switch account, Sign out.
 *
 * Sign out clears the MSAL cache for this app and ends the Entra session, returning to
 * the dashboard origin. (Signing out of Microsoft 365 elsewhere does not do this: the
 * dashboard keeps its own cached tokens until they are cleared or expire.)
 */
export default function UserMenu() {
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0];
  const { canWrite, isLoading } = useWritePermission();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!account) return null;
  const name = account.name || account.username;
  const roleLabel = isLoading ? 'Checking role…' : canWrite ? 'Audit Admin' : 'Read-only';

  async function signOut(): Promise<void> {
    setAnchor(null);
    // Drop every cached token/account for this app first, then end the Entra session.
    await instance.clearCache();
    await instance.logoutRedirect({ account, postLogoutRedirectUri: window.location.origin });
  }

  function switchAccount(): void {
    setAnchor(null);
    void instance.loginRedirect({ ...loginRequest, prompt: 'select_account' });
  }

  return (
    <>
      <Tooltip title={`${name} — ${roleLabel}`}>
        <IconButton
          onClick={(event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
          size="small"
          aria-label="Account menu"
          aria-haspopup="menu"
          sx={{ ml: 1 }}
        >
          <Avatar sx={{ width: 30, height: 30, fontSize: '0.8rem', bgcolor: 'primary.main' }}>
            {initials(name)}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1, minWidth: 240 }}>
          <Typography variant="subtitle2">{name}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {account.username}
          </Typography>
          <Chip
            label={roleLabel}
            size="small"
            color={canWrite ? 'primary' : 'default'}
            sx={{ mt: 1 }}
          />
        </Box>
        <Divider />
        <MenuItem onClick={switchAccount}>
          <ListItemIcon>
            <SwitchAccountIcon fontSize="small" />
          </ListItemIcon>
          Switch account
        </MenuItem>
        <MenuItem onClick={() => void signOut()}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}
